'use client'
import Button from '@/components/Button'
import { useUserPrefs } from '@/context/UserPrefsContext'
import { Modal, ModalBody, ModalContent, ModalHeader, useDisclosure } from '@heroui/react'
import { Bug } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

interface LogEntry {
  type: 'WARN' | 'ERROR' | 'INFO' | 'DEBUG'
  elapsedTimeSeconds: number
  elapsedTimeMilliseconds: number
  data: unknown[]
  functionName: string
  fileName: string
  lineNumber: string
}

interface StackInfo {
  functionName: string
  fileName: string
  lineNumber: string
  columnNumber: string
}

const startTime = Date.now()
const logs = {} as Record<string, LogEntry[]>

const addToStack = (stack: string, item: Omit<LogEntry, 'elapsedTimeSeconds' | 'elapsedTimeMilliseconds'>) => {
  if (!logs[stack]) logs[stack] = []

  const currentTime = Date.now()
  const seconds = Math.floor((currentTime - startTime) / 1000)
  const milliseconds = Math.floor((currentTime - startTime) % 1000)
  logs[stack].push({
    ...item,
    elapsedTimeSeconds: seconds,
    elapsedTimeMilliseconds: milliseconds,
  })

  if (logs[stack].length > 50) {
    logs[stack].shift()
  }

  // Dispatch event to notify listeners (LoggerModal) of new log
  window.dispatchEvent(new Event('logger:new-log'))
}

const getStackInfo = (): StackInfo => {
  // Get the full stack trace
  const stackLines = new Error().stack?.split('\n') || []

  // Skip the first line which is "Error" and look for the first frame outside of Logger.tsx
  let callerLine = ''

  // Start from index 1 to skip the 'Error' line
  for (let i = 1; i < stackLines.length; i++) {
    const line = stackLines[i]
    // Skip frames from this file (Logger.tsx) or from the console overrides
    if (line.includes('Logger.tsx') || line.includes('at console.') || line.includes('at getStackInfo')) {
      continue
    }
    // Found a line that's not from Logger.tsx
    callerLine = line
    break
  }

  // If we couldn't find a non-Logger line, fall back to the first non-Error line
  if (!callerLine && stackLines.length > 1) {
    callerLine = stackLines[1]
  }

  // Parse the line to extract function, file, and line info
  const match = callerLine.match(/at\s+(.+)\s+\((.+):(\d+):(\d+)\)/)

  // If the line format didn't match the pattern, try an alternative format
  // (for anonymous functions or top-level code)
  const altMatch = !match ? callerLine.match(/at\s+(.+):(\d+):(\d+)/) : null

  // Declare variables separately
  let functionName = ''
  let fileName = ''
  let lineNumber = ''
  let columnNumber = ''

  if (match) {
    // Standard format: "at functionName (file:line:column)"
    const matchedGroups = match || []
    // Skip first element (the full match)
    functionName = matchedGroups[1] || ''
    fileName = matchedGroups[2] || ''
    lineNumber = matchedGroups[3] || ''
    columnNumber = matchedGroups[4] || ''
  } else if (altMatch) {
    // Alternative format: "at file:line:column"
    const altMatchedGroups = altMatch || []
    fileName = altMatchedGroups[1] || ''
    lineNumber = altMatchedGroups[2] || ''
    columnNumber = altMatchedGroups[3] || ''
    functionName = '(anonymous)'
  }

  return {
    functionName,
    fileName: fileName.split('/').pop() || '',
    lineNumber,
    columnNumber,
  }
}

export default function ConsoleOverride() {
  const { userPrefs } = useUserPrefs()
  useEffect(() => {
    // Don't initialize anything if not in debug mode
    if (!userPrefs.debugMode) {
      return
    }

    const originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
      debug: console.debug,
      dir: console.dir,
    }

    // Override console methods
    console.log = (args: unknown[], stack = 'default') => {
      let data = args
      if (!Array.isArray(args)) {
        data = [args]
      }

      const { fileName, lineNumber, functionName } = getStackInfo()

      if (stack === 'default') {
        originalConsole.log(...data)
      }

      addToStack(stack, {
        type: 'INFO',
        data,
        fileName,
        lineNumber,
        functionName,
      })
    }

    console.warn = (args: unknown[], stack = 'default') => {
      let data = args
      if (!Array.isArray(args)) {
        data = [args]
      }

      const { fileName, lineNumber, functionName } = getStackInfo()

      if (stack === 'default') {
        originalConsole.warn(...data)
      }

      addToStack(stack, {
        type: 'WARN',
        data,
        fileName,
        lineNumber,
        functionName,
      })
    }

    console.error = (args: unknown[], stack = 'default') => {
      let data = args
      if (!Array.isArray(args)) {
        data = [args]
      }

      const { fileName, lineNumber, functionName } = getStackInfo()

      if (stack === 'default') {
        originalConsole.error(...data)
      }

      addToStack(stack, {
        type: 'ERROR',
        data,
        fileName,
        lineNumber,
        functionName,
      })
    }

    console.debug = (args: unknown[], stack = 'default') => {
      let data = args
      if (!Array.isArray(args)) {
        data = [args]
      }

      const { fileName, lineNumber, functionName } = getStackInfo()

      if (stack === 'default') {
        originalConsole.debug(...data)
      }

      addToStack(stack, {
        type: 'DEBUG',
        data,
        fileName,
        lineNumber,
        functionName,
      })
    }

    console.dir = (obj: unknown, options: unknown = { depth: null }) => {
      originalConsole.dir(obj, options)
    }

    originalConsole.log('Console override initialized. Logs will be captured.')

    // Cleanup function to restore original console methods
    return () => {
      console.log = originalConsole.log
      console.error = originalConsole.error
      console.debug = originalConsole.debug
      console.dir = originalConsole.dir
      console.warn = originalConsole.warn
    }
  }, [userPrefs.debugMode])

  if (!userPrefs.debugMode) return null
  return <LoggerToolbar />
}
export function LoggerToolbar() {
  const { isOpen, onOpen, onOpenChange } = useDisclosure()
  const [isDesktop, setIsDesktop] = useState(false)

  useEffect(() => {
    // Check if desktop on mount and window resize
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 1280)
    }

    checkDesktop()
    window.addEventListener('resize', checkDesktop)

    return () => {
      window.removeEventListener('resize', checkDesktop)
    }
  }, [])

  useEffect(() => {
    // Add/remove class to body for desktop layout shift
    if (isOpen && isDesktop) {
      document.body.classList.add('logger-open')
    } else {
      document.body.classList.remove('logger-open')
    }
    return () => {
      document.body.classList.remove('logger-open')
    }
  }, [isOpen, isDesktop])

  return (
    <>
      <Button
        base
        onClick={onOpen}
        className="fixed z-50 flex gap-2 p-4 m-4 border rounded-lg shadow-lg bottom-[calc(50%-29px)] right-1 bg-zinc-800 border-zinc-600 hover:bg-zinc-700"
      >
        <span className="text-cyan-400">
          <Bug />
        </span>
      </Button>

      {/* Desktop: Fixed sidebar panel */}
      {isDesktop && (
        <div
          className={`fixed top-0 right-0 bottom-0 w-[500px] z-[100] transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
        >
          <div className="flex flex-col h-full border-l shadow-2xl bg-zinc-900 border-zinc-700">
            <div className="flex items-center justify-between gap-1 p-4 text-white border-b border-zinc-700">
              <h2 className="text-lg font-semibold">Debug Logs</h2>
              <Button base onClick={() => onOpenChange()} className="p-2 rounded hover:bg-zinc-700">
                <span className="text-white">✕</span>
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto bg-zinc-900">
              <LoggerModal />
            </div>
          </div>
        </div>
      )}

      {/* Mobile: Modal */}
      {!isDesktop && (
        <Modal isOpen={isOpen} onOpenChange={onOpenChange} size={'5xl'} placement={'top'}>
          <ModalContent className="border bg-zinc-900 border-zinc-700">
            {(onClose) => (
              <>
                <ModalHeader className="flex flex-col gap-1 text-white border-b border-zinc-700">
                  Debug Logs
                </ModalHeader>
                <ModalBody className="max-h-[80vh] overflow-y-auto bg-zinc-900">
                  <LoggerModal />
                </ModalBody>
              </>
            )}
          </ModalContent>
        </Modal>
      )}

      {/* Global styles for layout shift */}
      <style jsx global>{`
        @media (min-width: 1280px) {
          body.logger-open {
            margin-right: 500px;
            transition: margin-right 0.3s ease;
          }
        }
      `}</style>
    </>
  )
}

export function LoggerModal() {
  const [selectedStack, setSelectedStack] = useState<string>('ALL_STACKS') // Changed default to ALL_STACKS
  const [typeFilter, setTypeFilter] = useState<'ALL' | LogEntry['type']>('ALL')
  const [search, setSearch] = useState('')
  const [refreshTrigger, setRefreshTrigger] = useState(0) // State to trigger re-render

  useEffect(() => {
    const handler = () => setRefreshTrigger((prev) => prev + 1)
    window.addEventListener('logger:new-log', handler)
    return () => window.removeEventListener('logger:new-log', handler)
  }, [])

  const stackNames = Object.keys(logs)

  const filtered = useMemo(() => {
    const allEntries: (LogEntry & { stackName: string })[] = []

    if (selectedStack === 'ALL_STACKS') {
      for (const [stackName, entries] of Object.entries(logs)) {
        for (const entry of entries) {
          allEntries.push({ ...entry, stackName })
        }
      }
    } else {
      const entries = logs[selectedStack] || []
      for (const entry of entries) {
        allEntries.push({ ...entry, stackName: selectedStack })
      }
    }

    // Sort by elapsedTimeSeconds and elapsedTimeMilliseconds (newest at the top)
    const sortedEntries = [...allEntries].sort((a, b) => {
      if (a.elapsedTimeSeconds !== b.elapsedTimeSeconds) {
        return b.elapsedTimeSeconds - a.elapsedTimeSeconds
      }
      return b.elapsedTimeMilliseconds - a.elapsedTimeMilliseconds
    })

    return sortedEntries.filter(
      (entry) =>
        (typeFilter === 'ALL' || entry.type === typeFilter) &&
        (search === '' || entry.data.some((d) => String(d).toLowerCase().includes(search.toLowerCase())))
    )
  }, [selectedStack, typeFilter, search, refreshTrigger])

  return (
    <div className="flex flex-col text-white bg-zinc-900">
      <div className="flex gap-2 p-2 mb-2 border-b border-zinc-700">
        <select
          value={selectedStack}
          onChange={(e) => setSelectedStack(e.target.value)}
          className="px-2 py-1 text-white border rounded bg-zinc-800 border-zinc-600"
        >
          <option value="ALL_STACKS">All Stacks</option> {/* Added All Stacks option */}
          {stackNames.map((name) => (
            <option key={name} value={name}>
              {name.substring(0, 20) || 'default'}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as LogEntry['type'])}
          className="px-2 py-1 text-white border rounded bg-zinc-800 border-zinc-600"
        >
          <option value="ALL">All</option>
          <option value="INFO">Info</option>
          <option value="WARN">Warn</option>
          <option value="ERROR">Error</option>
          <option value="DEBUG">Debug</option>
        </select>
        <input
          type="text"
          placeholder="Search logs..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 px-2 py-1 text-white border rounded bg-zinc-800 border-zinc-600 placeholder-zinc-400"
        />
      </div>

      <div className="flex-1 p-0.5 overflow-y-auto font-mono text-xs bg-zinc-900">
        {filtered.length === 0 && <div className="text-gray-400">No logs found.</div>}
        {filtered.map((entry, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey:
          <div key={i} className="p-2 mb-2 border rounded shadow-sm bg-zinc-800">
            <div className="flex items-center gap-2 mb-1">
              {selectedStack === 'ALL_STACKS' && ( // Conditionally display stack name
                <span className="text-xs font-bold text-fuchsia-400">[{entry.stackName}]</span>
              )}
              <span
                className={`text-xs font-bold ${
                  entry.type === 'INFO'
                    ? 'text-cyan-400'
                    : entry.type === 'WARN'
                      ? 'text-amber-400'
                      : entry.type === 'ERROR'
                        ? 'text-rose-400'
                        : 'text-emerald-400'
                }`}
              >
                [{entry.type}]
              </span>
              <span className="text-gray-300">
                {entry.fileName}:{entry.lineNumber} in {entry.functionName}
              </span>
              <span className="ml-auto text-gray-400">
                +{entry.elapsedTimeSeconds}s {entry.elapsedTimeMilliseconds}ms
              </span>
            </div>
            <div className="text-gray-200 break-all whitespace-pre-wrap">
              {entry.data.length !== 0 &&
                entry.data.map && // biome-ignore lint/suspicious/noArrayIndexKey:
                entry.data.map((d, j) => <span key={j}>{JSON.stringify(d)} </span>)}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export { ConsoleOverride }
